import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { supabase } from '@/lib/supabase'
import type { User } from '@supabase/supabase-js'

export const useLimitStore = defineStore('limit', () => {
    const authStore = useAuthStore()

    const showGuestDialog = ref(false)
    const showPremiumDialog = ref(false)
    const guestDialogVariant = ref<'limit' | 'premium'>('limit')
    const premiumDialogVariant = ref<'limit' | 'feature'>('limit')
    const isVerifying = ref(false)
    const availableLimit = ref<number | null>(null)
    
    // Asl foydalanuvchi premium statusi
    const isUserPremium = ref(false)
    
    // Promo/chegirma sozlamalari
    const promoConfig = ref({ is_active: false, start_date: null as string | null, end_date: null as string | null })

    const isPromoActive = computed(() => {
        if (!promoConfig.value.is_active) return false
        const now = Date.now()
        const start = promoConfig.value.start_date ? new Date(promoConfig.value.start_date).getTime() : null
        const end = promoConfig.value.end_date ? new Date(promoConfig.value.end_date).getTime() : null

        if (start && now < start) return false
        if (end && now > end) return false
        return true
    })

    // Premium status: haqiqiy premium yoki chegirma kunlari bo'lsa va foydalanuvchi tizimga kirgan bo'lsa yoki endi 1-chi rezyumesini yaratayotgan mehmon
    const isPremiumPlan = computed(() => {
        if (isUserPremium.value) return true
        if (isPromoActive.value) {
            if (authStore.user !== null) return true
            // Mehmon uchun, agar umuman CV yaratmagan bo'lsa (0) -> Premium!
            return getGuestCount() < 1
        }
        return false
    })

    let planStatusPromise: Promise<void> | null = null
    let lastPlanStatusAt = 0

    async function loadPromoSettings() {
        try {
            const { data, error } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'premium_promo')
                .single()

            if (data && !error) {
                promoConfig.value = {
                    is_active: data.value.is_active ?? false,
                    start_date: data.value.start_date ?? null,
                    end_date: data.value.end_date ?? null
                }
            }
        } catch (e) {
            console.error('Error loading promo settings:', e)
        }
    }

    function setPlanState(user: User | null) {
        isUserPremium.value = user?.user_metadata?.is_premium === true
    }

    function setGuestLimitState() {
        isUserPremium.value = false
        availableLimit.value = Math.max(0, 1 - getGuestCount())
    }

    function setUserLimitState(count: number | null, latestCreatedAt?: string) {
        if (isPremiumPlan.value) {
            availableLimit.value = 999
            return
        }

        if (count === null) {
            availableLimit.value = 0
            return
        }

        if (count < 1) {
            availableLimit.value = 1 - count
            return
        }

        if (!latestCreatedAt) {
            availableLimit.value = 0
            return
        }

        const diffInHours = (Date.now() - new Date(latestCreatedAt).getTime()) / (1000 * 60 * 60)
        availableLimit.value = diffInHours >= 24 ? 1 : 0
    }

    const isLoadingPlan = ref(true)

    async function getAuthUser(force = false): Promise<User | null> {
        if (!force && authStore.user) {
            return authStore.user
        }

        const { data: { user } } = await supabase.auth.getUser()

        if (user && authStore.user) {
            authStore.user.user_metadata = user.user_metadata
        }

        return user ?? authStore.user ?? null
    }

    async function loadPlanStatus(force = false) {
        const now = Date.now()

        if (planStatusPromise) {
            return planStatusPromise
        }

        if (!force && now - lastPlanStatusAt < 10000) {
            return
        }

        isLoadingPlan.value = true
        planStatusPromise = (async () => {
        try {
            // Promo sozlamalarini yuklaymiz
            await loadPromoSettings()

            // Agar force bo'lmasa, cache qilingan authStore.user dan foydalanishga harakat qilamiz, 
            // supabase.auth.getUser() esa har safar API chaqirmasligi uchun
            const user = await getAuthUser(force) 

            if (!user) {
                setGuestLimitState()
                return
            }

            if (authStore.user) {
                authStore.user.user_metadata = user.user_metadata
            }
            
            // Sync with actual profiles table to reflect real-time plan logic changed by Admins
            const { data: profile } = await supabase
                .from('profiles')
                .select('is_premium')
                .eq('id', user.id)
                .single()
                
            if (profile) {
                isUserPremium.value = profile.is_premium === true
                // Update local metadata to match
                if (authStore.user) {
                    authStore.user.user_metadata = { ...authStore.user.user_metadata, is_premium: profile.is_premium === true }
                }
            } else {
                setPlanState(user)
            }
            
            const { data: resumes, count } = await supabase
                .from('resumes')
                .select('created_at', { count: 'exact' })
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(1)

            setUserLimitState(count, resumes?.[0]?.created_at)
            lastPlanStatusAt = Date.now()
        } catch (e) {
            console.error(e)
        } finally {
            planStatusPromise = null
            isLoadingPlan.value = false
        }
        })()

        return planStatusPromise
    }

    async function checkCanCreate(silent = false): Promise<boolean> {
        try {
            // Promo sozlamalarini yangilaymiz
            await loadPromoSettings()

            const supabaseUser = await getAuthUser(true) // Force fetch to avoid stale token

            if (!supabaseUser && !authStore.user) {
                // Mehmon uchun tekshiruv
                const count = getGuestCount()
                setGuestLimitState()
                if (count >= 1) {
                    if (!silent) openGuestDialog('limit')
                    return false
                }
                return true
            } else {
                // Tizimga kirgan foydalanuvchi uchun tekshiruv
                const userId = supabaseUser?.id || authStore.user?.id
                
                // Refresh its premium status deeply just in case admin updated it
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('is_premium')
                    .eq('id', userId)
                    .single()
                    
                if (profile) {
                    isUserPremium.value = profile.is_premium === true
                    if (authStore.user) {
                       authStore.user.user_metadata = { ...authStore.user.user_metadata, is_premium: profile.is_premium === true }
                    }
                } else {
                    setPlanState(supabaseUser)
                }
                
                // Agar haqiqiy premium yoki promo faol bo'lsa darhol true qaytaramiz
                if (isPremiumPlan.value) {
                    return true
                }
                
                // 1. Umumiy yaratilgan CVlar soni va oxirgisining datasini olamiz
                const { data: resumes, count, error } = await supabase
                    .from('resumes')
                    .select('created_at', { count: 'exact' })
                    .eq('user_id', userId)
                    .order('created_at', { ascending: false })
                    .limit(1)

                if (error) {
                    console.error('Error fetching resumes count:', error)
                    return true // Xatolik bo'lsa o'tkazib yuboramiz
                }

                // Premium statusini eng so'nggi ma'lumot qilib serverdan tortamiz! (Token keshlanib qolishini olish logikasi)
                let isPremium = false
                
                if (supabaseUser) {
                    if (authStore.user) {
                        authStore.user.user_metadata = supabaseUser.user_metadata
                    }
                    isPremium = supabaseUser.user_metadata?.is_premium === true
                } else if (authStore.user) {
                    isPremium = authStore.user.user_metadata?.is_premium === true
                }

                isUserPremium.value = isPremium
                setUserLimitState(count, resumes?.[0]?.created_at)

                // Agar u premium bo'lmasa va bazada allaqachon 1 yoki undan ko'p CV bo'lsa
                if (!isPremium && count !== null && count >= 1) {
                    // Agar oxirgi CVsi bor bo'lsa, uni qachon yaratganini tekshiramiz
                    if (resumes && resumes.length > 0) {
                        const lastCreatedAt = new Date(resumes[0].created_at).getTime()
                        const now = new Date().getTime()
                        const diffInHours = (now - lastCreatedAt) / (1000 * 60 * 60)

                        // Agar oxirgi CV yaratilganiga 24 soat to'lmagan bo'lsa - O'TKAZMAYMIZ (LimitDialog)
                        if (diffInHours < 24) {
                            if (!silent) {
                                premiumDialogVariant.value = 'limit'
                                showPremiumDialog.value = true
                            }
                            return false
                        }
                        
                        // DIQQAT: Agar 24 soat o'tgan bo'lsa u bemalol yaratishda davom etaveradi (pastga o'tib true qaytaradi)
                    } else {
                         // Garchi sanasi xato bo'lsayu count >= 1 bo'lsa ham yopamiz (xavfsizlik uchun)
                         if (!silent) {
                             premiumDialogVariant.value = 'limit'
                             showPremiumDialog.value = true
                         }
                         return false
                    }
                }
                return true
            }
        } catch (e) {
            console.error(e)
            return false
        }
    }

    function getGuestCount(): number {
        try {
            // Eskicha qolgan ma'lumotni tozalash
            localStorage.removeItem('guest_cv_count')
            const val = localStorage.getItem('_cv_gen_t')
            if (!val) return 0
            return parseInt(atob(val)) || 0
        } catch {
            return 0
        }
    }

    function incrementGuestCount() {
        if (!authStore.user) {
            const count = getGuestCount()
            localStorage.setItem('_cv_gen_t', btoa((count + 1).toString()))
        }
    }

    function closeDialogs() {
        showGuestDialog.value = false
        showPremiumDialog.value = false
        guestDialogVariant.value = 'limit'
    }

    function openGuestDialog(variant: 'limit' | 'premium' = 'limit') {
        guestDialogVariant.value = variant
        showPremiumDialog.value = false
        showGuestDialog.value = true
    }

    function openPremiumAccessDialog(variant: 'limit' | 'feature' = 'limit') {
        if (authStore.user) {
            showGuestDialog.value = false
            premiumDialogVariant.value = variant
            showPremiumDialog.value = true
            return
        }

        openGuestDialog('premium')
    }

    async function verifyPayment(): Promise<boolean> {
        isVerifying.value = true
        try {
            const user = await getAuthUser(true)
            
            if (user && user.user_metadata?.is_premium === true) {
                if (authStore.user) {
                    authStore.user.user_metadata = user.user_metadata
                }
                setPlanState(user)
                availableLimit.value = 999
                return true
            }
            // Add a small delay so the user perceives a check if it's too fast
            await new Promise(r => setTimeout(r, 1500))
            return false
        } catch (e) {
            console.error(e)
            return false
        } finally {
            isVerifying.value = false
        }
    }

    return {
        showGuestDialog,
        showPremiumDialog,
        guestDialogVariant,
        premiumDialogVariant,
        isVerifying,
        isLoadingPlan,
        availableLimit,
        isPremiumPlan,
        isUserPremium,
        promoConfig,
        isPromoActive,
        loadPromoSettings,
        loadPlanStatus,
        checkCanCreate,
        incrementGuestCount,
        closeDialogs,
        openGuestDialog,
        openPremiumAccessDialog,
        verifyPayment
    }
})
